import React, { useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const allColumns = [
  { key: 'id', label: 'Shipment ID' },
  { key: 'origin', label: 'Origin' },
  { key: 'destination', label: 'Destination' },
  { key: 'status', label: 'Status' },
  { key: 'eta', label: 'ETA' },
  { key: 'consignee', label: 'Consignee' },
  { key: 'consignor', label: 'Consignor' },
  { key: 'departurePort', label: 'Departure Port' },
  { key: 'arrivalPort', label: 'Arrival Port' },
  { key: 'originCountry', label: 'Country of Origin' },
  { key: 'destCountry', label: 'Country of Destination' },
  { key: 'value', label: 'Value (USD)' },
  { key: 'container', label: 'Container Number' },
  { key: 'weight', label: 'Weight (kg)' },
  { key: 'packages', label: 'Packages' },
  { key: 'carrier', label: 'Carrier' },
  { key: 'vessel', label: 'Vessel Name' },
  { key: 'blNumber', label: 'Bill of Lading Number' },
  { key: 'notes', label: 'Notes' }
];

const defaultColumns = ['id', 'origin', 'destination', 'status', 'eta'];

const initialFreightData = [
  {
    id: 'FF001',
    origin: 'Shanghai',
    destination: 'Los Angeles',
    status: 'In Transit',
    eta: '2024-06-10',
    consignee: 'ABC Corp',
    consignor: 'XYZ Ltd',
    departurePort: 'Shanghai',
    arrivalPort: 'Los Angeles',
    originCountry: 'China',
    destCountry: 'USA',
    value: '15000',
    container: 'MSKU1234567',
    weight: '800',
    packages: '12',
    carrier: 'Maersk',
    vessel: 'Maersk Alabama',
    blNumber: 'BL123456',
    notes: 'Fragile'
  },
  {
    id: 'FF002',
    origin: 'Hamburg',
    destination: 'New York',
    status: 'Arrived',
    eta: '2024-06-05',
    consignee: 'DEF Inc',
    consignor: 'LMN Exporters',
    departurePort: 'Hamburg',
    arrivalPort: 'New York',
    originCountry: 'Germany',
    destCountry: 'USA',
    value: '9000',
    container: 'CMAU7654321',
    weight: '400',
    packages: '8',
    carrier: 'CMA CGM',
    vessel: 'CMA CGM Marco Polo',
    blNumber: 'BL654321',
    notes: ''
  },
  {
    id: 'FF003',
    origin: 'Singapore',
    destination: 'Dubai',
    status: 'Delayed',
    eta: '2024-06-12',
    consignee: 'GHI Logistics',
    consignor: 'Asia Freight',
    departurePort: 'Singapore',
    arrivalPort: 'Dubai',
    originCountry: 'Singapore',
    destCountry: 'UAE',
    value: '20000',
    container: 'HLCU9988776',
    weight: '1200',
    packages: '20',
    carrier: 'Hapag-Lloyd',
    vessel: 'Hapag-Lloyd Express',
    blNumber: 'BL998877',
    notes: 'Check documents'
  }
];

const emptyForm = {
  id: '',
  origin: '',
  destination: '',
  status: '',
  eta: '',
  consignee: '',
  consignor: '',
  departurePort: '',
  arrivalPort: '',
  originCountry: '',
  destCountry: '',
  value: '',
  container: '',
  weight: '',
  packages: '',
  carrier: '',
  vessel: '',
  blNumber: '',
  notes: ''
};

function FreightForwarding() {
  const [freightData, setFreightData] = useState(initialFreightData);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [showColumnsModal, setShowColumnsModal] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(defaultColumns);
  const [fraudModal, setFraudModal] = useState({ open: false, result: null });
  const [docModal, setDocModal] = useState({ open: false, shipment: null, docType: 'Invoice' });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    for (const key of Object.keys(form)) {
      if (key !== 'notes' && !form[key]) {
        setError('All fields except Notes are required');
        return;
      }
    }
    if (editIndex !== null) {
      const updated = freightData.slice();
      updated[editIndex] = form;
      setFreightData(updated);
      setEditIndex(null);
    } else {
      setFreightData([...freightData, form]);
    }
    setForm(emptyForm);
    setError('');
    setShowModal(false);
  };

  const handleEdit = (index) => {
    setForm(freightData[index]);
    setEditIndex(index);
    setShowModal(true);
  };

  const handleDelete = (index) => {
    if (window.confirm('Are you sure you want to delete this shipment?')) {
      setFreightData(freightData.filter((_, i) => i !== index));
    }
  };

  const handleColumnChange = (key) => {
    setVisibleColumns((cols) =>
      cols.includes(key)
        ? cols.filter((c) => c !== key)
        : [...cols, key]
    );
  };

  function analyzeFraud(shipment) {
    // Dummy AI logic
    let score = 0;
    let reasons = [];
    if (shipment.value && Number(shipment.value) > 15000) {
      score += 0.5;
      reasons.push('High shipment value');
    }
    if (shipment.originCountry && ['North Korea', 'Iran', 'Syria'].includes(shipment.originCountry)) {
      score += 0.6;
      reasons.push('Origin country flagged as high risk');
    }
    if (shipment.status === 'Delayed') {
      score += 0.2;
      reasons.push('Shipment status is Delayed');
    }
    let result = {
      fraud: score > 0.7,
      confidence: Math.min(99, Math.round(score * 100)),
      reasons: reasons.length ? reasons : ['No suspicious indicators found.']
    };
    setFraudModal({ open: true, result });
  }

  function handlePrintDoc() {
    const printContents = document.getElementById('print-doc-content').innerHTML;
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write('<html><head><title>Print Document</title>');
    printWindow.document.write('</head><body >');
    printWindow.document.write(printContents);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  }

  async function handleDownloadPDF() {
    const input = document.getElementById('print-doc-content');
    const canvas = await html2canvas(input, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth - 40;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    pdf.addImage(imgData, 'PNG', 20, 20, imgWidth, imgHeight);
    pdf.save(`${docModal.docType}_${docModal.shipment?.id}.pdf`);
  }

  function renderDocument(shipment, docType) {
    if (!shipment) return null;
    const logoUrl = "https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/1f69a.svg";
    return (
      <div style={{ fontFamily: 'Arial', color: '#222', padding: 24, maxWidth: 500 }}>
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <img src={logoUrl} alt="Logo" style={{ width: 48, height: 48, marginBottom: 4 }} />
        </div>
        {(() => {
          switch (docType) {
            case 'Invoice':
              return (
                <>
                  <h2 style={{ textAlign: 'center', marginBottom: 8 }}>INVOICE</h2>
                  <hr />
                  <div style={{ marginBottom: 12 }}><b>Shipment ID:</b> {shipment.id}</div>
                  <div style={{ marginBottom: 12 }}><b>Consignee:</b> {shipment.consignee}</div>
                  <div style={{ marginBottom: 12 }}><b>Consignor:</b> {shipment.consignor}</div>
                  <div style={{ marginBottom: 12 }}><b>Description:</b> {shipment.description}</div>
                  <div style={{ marginBottom: 12 }}><b>Value (USD):</b> {shipment.value}</div>
                  <div style={{ marginBottom: 12 }}><b>Date:</b> {shipment.eta}</div>
                  <div style={{ marginBottom: 12 }}><b>Origin:</b> {shipment.origin}</div>
                  <div style={{ marginBottom: 12 }}><b>Destination:</b> {shipment.destination}</div>
                  <div style={{ marginBottom: 12 }}><b>Container #:</b> {shipment.container}</div>
                  <div style={{ marginBottom: 12 }}><b>Weight (kg):</b> {shipment.weight}</div>
                  <div style={{ marginBottom: 12 }}><b>Packages:</b> {shipment.packages}</div>
                  <div style={{ marginBottom: 12 }}><b>Carrier:</b> {shipment.carrier}</div>
                </>
              );
            case 'Bill of Lading':
              return (
                <>
                  <h2 style={{ textAlign: 'center', marginBottom: 8 }}>BILL OF LADING</h2>
                  <hr />
                  <div style={{ marginBottom: 12 }}><b>Shipment ID:</b> {shipment.id}</div>
                  <div style={{ marginBottom: 12 }}><b>Consignee:</b> {shipment.consignee}</div>
                  <div style={{ marginBottom: 12 }}><b>Consignor:</b> {shipment.consignor}</div>
                  <div style={{ marginBottom: 12 }}><b>Origin:</b> {shipment.origin}</div>
                  <div style={{ marginBottom: 12 }}><b>Destination:</b> {shipment.destination}</div>
                  <div style={{ marginBottom: 12 }}><b>Carrier:</b> {shipment.carrier}</div>
                  <div style={{ marginBottom: 12 }}><b>Packages:</b> {shipment.packages}</div>
                  <div style={{ marginBottom: 12 }}><b>Weight (kg):</b> {shipment.weight}</div>
                  <div style={{ marginBottom: 12 }}><b>Date:</b> {shipment.eta}</div>
                </>
              );
            case 'Packing List':
              return (
                <>
                  <h2 style={{ textAlign: 'center', marginBottom: 8 }}>PACKING LIST</h2>
                  <hr />
                  <div style={{ marginBottom: 12 }}><b>Shipment ID:</b> {shipment.id}</div>
                  <div style={{ marginBottom: 12 }}><b>Description:</b> {shipment.description}</div>
                  <div style={{ marginBottom: 12 }}><b>Packages:</b> {shipment.packages}</div>
                  <div style={{ marginBottom: 12 }}><b>Weight (kg):</b> {shipment.weight}</div>
                  <div style={{ marginBottom: 12 }}><b>Date:</b> {shipment.eta}</div>
                </>
              );
            case 'Freight Manifest':
              return (
                <>
                  <h2 style={{ textAlign: 'center', marginBottom: 8 }}>FREIGHT MANIFEST</h2>
                  <hr />
                  <div style={{ marginBottom: 12 }}><b>Shipment ID:</b> {shipment.id}</div>
                  <div style={{ marginBottom: 12 }}><b>Consignee:</b> {shipment.consignee}</div>
                  <div style={{ marginBottom: 12 }}><b>Consignor:</b> {shipment.consignor}</div>
                  <div style={{ marginBottom: 12 }}><b>Origin:</b> {shipment.origin}</div>
                  <div style={{ marginBottom: 12 }}><b>Destination:</b> {shipment.destination}</div>
                  <div style={{ marginBottom: 12 }}><b>Container #:</b> {shipment.container}</div>
                  <div style={{ marginBottom: 12 }}><b>Carrier:</b> {shipment.carrier}</div>
                  <div style={{ marginBottom: 12 }}><b>Weight (kg):</b> {shipment.weight}</div>
                  <div style={{ marginBottom: 12 }}><b>Packages:</b> {shipment.packages}</div>
                  <div style={{ marginBottom: 12 }}><b>Date:</b> {shipment.eta}</div>
                </>
              );
            default:
              return null;
          }
        })()}
      </div>
    );
  }

  return (
    <div>
      <h2>Freight Forwarding Module</h2>
      <img
        src="https://images.unsplash.com/photo-1506619216599-9d16d0903dfd?auto=format&fit=crop&w=600&q=80"
        alt="Freight Forwarding"
        style={{ width: 320, borderRadius: 10, marginBottom: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.10)' }}
      />
      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
        <button onClick={() => { setShowModal(true); setEditIndex(null); setForm(emptyForm); }} style={{ padding: '8px 20px', fontSize: 16, background: '#2e8b57', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Add Shipment</button>
        <button onClick={() => setShowColumnsModal(true)} style={{ padding: '8px 20px', fontSize: 16, background: '#1a355e', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>Customize Columns</button>
      </div>
      {showColumnsModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: '#fff', padding: 32, borderRadius: 12, minWidth: 320, maxWidth: 400, boxShadow: '0 4px 24px rgba(0,0,0,0.15)', position: 'relative' }}>
            <button onClick={() => setShowColumnsModal(false)} style={{ position: 'absolute', top: 10, right: 16, background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>&times;</button>
            <h3 style={{ marginTop: 0 }}>Select Columns to Display</h3>
            <form>
              {allColumns.map(col => (
                <div key={col.key} style={{ marginBottom: 8 }}>
                  <label>
                    <input
                      type="checkbox"
                      checked={visibleColumns.includes(col.key)}
                      onChange={() => handleColumnChange(col.key)}
                    />{' '}
                    {col.label}
                  </label>
                </div>
              ))}
            </form>
            <button onClick={() => setShowColumnsModal(false)} style={{ marginTop: 16, padding: '8px 20px', background: '#2e8b57', color: '#fff', border: 'none', borderRadius: 6, fontSize: 16, width: '100%' }}>Done</button>
          </div>
        </div>
      )}
      {showModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{ background: '#fff', padding: 32, borderRadius: 12, minWidth: 320, maxWidth: 500, boxShadow: '0 4px 24px rgba(0,0,0,0.15)', position: 'relative' }}>
            <button onClick={() => { setShowModal(false); setEditIndex(null); }} style={{ position: 'absolute', top: 10, right: 16, background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>&times;</button>
            <h3 style={{ marginTop: 0 }}>{editIndex !== null ? 'Edit Freight Shipment' : 'Add Freight Shipment'}</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {allColumns.map(col => (
                  <input
                    key={col.key}
                    name={col.key}
                    placeholder={col.label}
                    value={form[col.key] || ''}
                    onChange={handleChange}
                    style={inputStyle}
                  />
                ))}
              </div>
              {error && <div style={{ color: 'red', margin: '10px 0' }}>{error}</div>}
              <button type="submit" style={{ width: '100%', padding: 10, background: '#2e8b57', color: '#fff', border: 'none', borderRadius: 6, fontSize: 16, marginTop: 10 }}>{editIndex !== null ? 'Update Shipment' : 'Add Shipment'}</button>
            </form>
          </div>
        </div>
      )}
      <div style={{ overflowX: 'auto', width: '100%' }}>
        <table border="1" cellPadding="8" style={{ minWidth: 850, width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {allColumns.filter(col => visibleColumns.includes(col.key)).map(col => (
                <th key={col.key} style={{ minWidth: getMinWidth(col.key), background: '#f4f6fa' }}>{col.label}</th>
              ))}
              <th style={{ minWidth: 180, background: '#f4f6fa' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {freightData.map((item, idx) => (
              <tr key={item.id}>
                {allColumns.filter(col => visibleColumns.includes(col.key)).map(col => (
                  <td key={col.key} style={{ minWidth: getMinWidth(col.key) }}>{item[col.key]}</td>
                ))}
                <td style={{ minWidth: 180 }}>
                  <button onClick={() => handleEdit(idx)} style={{ marginRight: 8, padding: '4px 12px', background: '#ffc107', color: '#222', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Edit</button>
                  <button onClick={() => handleDelete(idx)} style={{ marginRight: 8, padding: '4px 12px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Delete</button>
                  <button onClick={() => analyzeFraud(item)} style={{ marginRight: 8, padding: '4px 12px', background: '#2e8b57', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Analyze for Fraud</button>
                  <button onClick={() => setDocModal({ open: true, shipment: item })} style={{ padding: '4px 12px', background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Documents</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Fraud Modal */}
      {fraudModal.open && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ background: '#fff', padding: 32, borderRadius: 12, minWidth: 320, maxWidth: 400, boxShadow: '0 4px 24px rgba(0,0,0,0.15)', position: 'relative' }}>
            <button onClick={() => setFraudModal({ open: false, result: null })} style={{ position: 'absolute', top: 10, right: 16, background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>&times;</button>
            <h3 style={{ marginTop: 0 }}>AI Fraud Detection Result</h3>
            <div style={{ margin: '18px 0', color: fraudModal.result.fraud ? '#dc3545' : '#2e8b57', fontWeight: 600, fontSize: 18 }}>
              {fraudModal.result.fraud ? 'Potential Fraud Detected' : 'No Fraud Detected'}
            </div>
            <div style={{ marginBottom: 10 }}>Confidence: <b>{fraudModal.result.confidence}%</b></div>
            <div style={{ color: '#555', fontSize: 15 }}>
              <b>Reason{fraudModal.result.reasons.length > 1 ? 's' : ''}:</b>
              <ul style={{ margin: '8px 0 0 18px' }}>
                {fraudModal.result.reasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </div>
          </div>
        </div>
      )}
      {/* Documents Modal */}
      {docModal.open && (
        <>
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100 }}>
            <div className="responsive-modal" style={{ background: '#fff', padding: 32, borderRadius: 12, minWidth: 340, maxWidth: 540, boxShadow: '0 4px 24px rgba(0,0,0,0.15)', position: 'relative', width: '90vw', maxHeight: '90vh', overflowY: 'auto' }}>
              <button aria-label="Close" onClick={() => setDocModal({ open: false, shipment: null, docType: 'Invoice' })} style={{ position: 'absolute', top: 10, right: 16, background: '#eaf0fa', border: 'none', fontSize: 22, cursor: 'pointer', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#1a355e', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>&times;</button>
              <h3 style={{ marginTop: 0 }}>Documents for {docModal.shipment?.id}</h3>
              <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
                {['Invoice', 'Bill of Lading', 'Packing List', 'Freight Manifest'].map(type => (
                  <button key={type} onClick={() => setDocModal(dm => ({ ...dm, docType: type }))} style={{ padding: '6px 12px', background: docModal.docType === type ? '#1a355e' : '#eaf0fa', color: docModal.docType === type ? '#fff' : '#1a355e', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 500 }}>{type}</button>
                ))}
              </div>
              <div id="print-doc-content">
                {renderDocument(docModal.shipment, docModal.docType)}
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                <button onClick={handlePrintDoc} style={{ flex: 1, padding: 10, background: '#1a355e', color: '#fff', border: 'none', borderRadius: 6, fontSize: 16 }}>Print</button>
                <button onClick={handleDownloadPDF} style={{ flex: 1, padding: 10, background: '#2e8b57', color: '#fff', border: 'none', borderRadius: 6, fontSize: 16 }}>Download PDF</button>
              </div>
            </div>
          </div>
          <style>{`
            @media (max-width: 600px) {
              .responsive-modal {
                min-width: 0 !important;
                max-width: 98vw !important;
                padding: 12px !important;
                border-radius: 8px !important;
              }
              .responsive-modal button, .responsive-modal input {
                font-size: 15px !important;
              }
              .responsive-modal .doc-tabs {
                flex-direction: column !important;
                gap: 4px !important;
              }
            }
          `}</style>
        </>
      )}
    </div>
  );
}

const inputStyle = {
  marginBottom: 10,
  width: 'calc(50% - 10px)',
  padding: 8
};

function getMinWidth(key) {
  switch (key) {
    case 'id': return 120;
    case 'origin': return 130;
    case 'destination': return 130;
    case 'status': return 110;
    case 'eta': return 120;
    default: return 110;
  }
}

export default FreightForwarding; 